/**
 * What the side-menu update line shows: up to date, a newer version live, or offline.
 *
 * It answers one question: is this phone running the version that's live on the server
 * (/version.json)? It deliberately does NOT follow the service-worker library's "update
 * downloaded" flag: that flag never clears until a full reload, so in v6.37.0 a phone
 * already on the newest version stayed stuck on "New version ready". Installing a
 * downloaded worker is background housekeeping (useServiceWorkerUpdate), not news.
 *
 * Module-level store (same pattern as syncSignals): the service-worker hook in App feeds
 * it, and the line reads it through useSyncExternalStore without re-rendering anything else.
 *
 * Releases no longer send a push notification (Oct 2026); this line is how anyone
 * can tell whether their phone has the newest version.
 */

import { hasBlockingPendingWork } from './syncSignals';

export type UpdateState = 'current' | 'ready' | 'offline';

let liveVersion: string | null = null;
let online = typeof navigator === 'undefined' ? true : navigator.onLine;
let applyNowHandler: (() => void) | null = null;

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const subscribeUpdateStatus = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Pure: true when the server's live version is newer than the running one. */
export const isNewerVersion = (live: string | null, running: string): boolean => {
  if (!live) return false;
  const a = live.split('.').map((n) => parseInt(n, 10) || 0);
  const b = running.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return false;
};

export const computeUpdateState = (
  input: { online: boolean; liveVersion: string | null },
  runningVersion: string
): UpdateState => {
  if (!input.online) return 'offline';
  if (isNewerVersion(input.liveVersion, runningVersion)) return 'ready';
  return 'current';
};

export const getUpdateInputs = () => ({ online, liveVersion });

export const setLiveVersion = (version: string | null): void => {
  if (version === liveVersion) return;
  liveVersion = version;
  notify();
};

export const setOnline = (value: boolean): void => {
  if (value === online) return;
  online = value;
  notify();
};

/** The service-worker hook registers how to install right now (the "Update now" tap). */
export const registerApplyNow = (handler: (() => void) | null): void => {
  applyNowHandler = handler;
};

export const applyUpdateNow = (): void => {
  if (applyNowHandler) return applyNowHandler();
  // No hook (the whole app crashed and unmounted it): keep its photo guard anyway.
  if (hasBlockingPendingWork()) {
    window.alert('A photo is still uploading. Tap Update now again in a moment.');
    return;
  }
  window.location.reload();
};

/** "just now", "5 min ago", "2h ago", "3 days ago". */
export const formatUpdatedAgo = (builtAt: string | undefined, now: number = Date.now()): string | null => {
  if (!builtAt) return null;
  const then = Date.parse(builtAt);
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.floor((now - then) / 60_000));
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Updated ${days} day${days === 1 ? '' : 's'} ago`;
};
